import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/api";
import { ProductCard, CategoryCard } from "@/components/Cards";
import SeoHead from "@/lib/SeoHead";
import { ArrowRight, Search } from "lucide-react";

export default function Products() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (slug) {
      api.get(`/categories/${slug}`).then((r) => setActiveCat(r.data)).catch(() => setActiveCat(null));
      api.get(`/products?category=${slug}&limit=200`).then((r) => { setProducts(r.data || []); setLoading(false); });
    } else {
      setActiveCat(null);
      api.get("/products?limit=200").then((r) => { setProducts(r.data || []); setLoading(false); });
    }
  }, [slug]);

  const filtered = products.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.short_description?.toLowerCase().includes(search.toLowerCase())
  );

  const seoTitle = activeCat?.seo_title || activeCat?.name ? `${activeCat?.name} | Durga Auto Industries` : "Industrial Fasteners | Durga Auto Industries";
  const seoDesc = activeCat?.seo_description || "Browse our complete range of industrial fasteners — bolts, nuts, screws, washers and studs.";

  return (
    <>
      <SeoHead title={seoTitle} description={seoDesc} keywords={activeCat?.seo_keywords} />

      {/* Page header */}
      <section className="bg-[#1C1C1C] text-white pt-16 pb-12 border-b border-[#333]" data-testid="products-header">
        <div className="container-x">
          <div className="overline mb-4">{activeCat ? "Category" : "Catalog"}</div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl">{activeCat ? activeCat.name : "All Fasteners"}</h1>
          {activeCat?.description && <p className="mt-4 body-lg text-white/70 font-body max-w-3xl">{activeCat.description}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-2 text-sm font-body text-white/60">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/products" className={!activeCat ? "text-white" : "hover:text-white"}>Products</Link>
            {activeCat && <><span>/</span><span className="text-white">{activeCat.name}</span></>}
          </div>
        </div>
      </section>

      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
          <aside className="space-y-2 lg:sticky lg:top-28 lg:self-start" data-testid="products-sidebar">
            <div className="overline mb-4">Categories</div>
            <Link to="/products" className={`block px-4 py-3 border-l-2 font-heading font-medium transition-colors ${!slug ? "border-[#C1121F] text-[#C1121F] bg-[#F5F5F5]" : "border-transparent text-[#1C1C1C] hover:border-[#C1121F] hover:text-[#C1121F]"}`}>
              All Products
            </Link>
            {categories.map((c) => (
              <Link key={c.id} to={`/products/category/${c.slug}`}
                className={`block px-4 py-3 border-l-2 font-heading font-medium transition-colors ${slug === c.slug ? "border-[#C1121F] text-[#C1121F] bg-[#F5F5F5]" : "border-transparent text-[#1C1C1C] hover:border-[#C1121F] hover:text-[#C1121F]"}`}
                data-testid={`sidebar-cat-${c.slug}`}>
                {c.name}
              </Link>
            ))}
          </aside>

          <div>
            <div className="mb-8 flex items-center gap-3 border border-[#E5E7EB] rounded-sm px-4 py-3">
              <Search size={18} className="text-[#A3A3A3]" />
              <input
                type="text"
                placeholder="Search products by name, grade, standard…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent outline-none font-body text-sm text-[#1C1C1C] placeholder:text-[#A3A3A3]"
                data-testid="products-search"
              />
              <span className="font-body text-sm text-[#A3A3A3]">{filtered.length} items</span>
            </div>

            {loading ? (
              <Skeleton />
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-[#E5E7EB] rounded-sm">
                <p className="font-body text-[#4B5563]">No products match your filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {!slug && categories.length > 0 && (
              <div className="mt-20">
                <h2 className="h-card text-[#1C1C1C] mb-6">Browse by category</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((c) => <CategoryCard key={c.id} category={c} />)}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="card-light overflow-hidden">
          <div className="aspect-[4/3] bg-[#F5F5F5] animate-pulse" />
          <div className="p-6 space-y-3">
            <div className="h-4 w-2/3 bg-[#F5F5F5] animate-pulse" />
            <div className="h-3 w-full bg-[#F5F5F5] animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}
